import React from 'react';
import './OptionSelector.css';
import DimensionSelector from './DimensionSelector';
import MaterialSelector from './MaterialSelector';
import AccessorySelectorGrouped from './AccessorySelectorGrouped';

/**
 * OptionSelector - Smart component that renders the appropriate selector
 * based on the step type for improved UX with long option lists
 */
const OptionSelector = ({ step, configuration, onSelect, onMultiSelect }) => {
  const selectedValue = configuration[step.key];
  const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];

  // Determine if we should use specialized selectors based on step key
  const stepKey = step.key;

  // Table Size - Use DimensionSelector for two-step depth/length selection
  if (stepKey === 'table_size' && step.options && step.options.length > 5) {
    return (
      <DimensionSelector
        step={step}
        configuration={configuration}
        onSelect={onSelect}
      />
    );
  }

  // Top Surface - Use MaterialSelector for tabbed material type selection
  if (stepKey === 'top_surface' && step.options && step.options.length > 10) {
    return (
      <MaterialSelector
        step={step}
        configuration={configuration}
        onSelect={onSelect}
      />
    );
  }

  // Accessories - Use AccessorySelectorGrouped for collapsible categories
  if (stepKey === 'accessories' && step.multiSelect && step.options && step.options.length > 5) {
    return (
      <AccessorySelectorGrouped
        step={step}
        configuration={configuration}
        onSelect={onSelect}
        onMultiSelect={onMultiSelect}
      />
    );
  }

  // Default handlers for fallback rendering
  const handleRadioChange = (value) => {
    onSelect(step.key, value);
  };

  const handleCheckboxChange = (value, checked) => {
    // Special handling for "no-accessories" option
    if (value === 'no-accessories') {
      if (checked) {
        // If "No Accessories" is checked, clear all other selections and set only this
        onSelect(step.key, ['no-accessories']);
      } else {
        // If unchecking "No Accessories", clear it
        onSelect(step.key, []);
      }
    } else {
      // If selecting any other accessory, remove "no-accessories" first
      if (selectedValues.includes('no-accessories')) {
        onSelect(step.key, checked ? [value] : []);
      } else {
        onMultiSelect(step.key, value, checked);
      }
    }
  };

  // Fallback: Multi-select (checkbox) rendering
  if (step.multiSelect) {
    return (
      <div className="option-selector">
        <p className="step-description">{step.description}</p>
        <div className="options-list">
          {step.options.map(option => (
            <div key={option.value} className="option-item checkbox-option">
              <label className="option-label">
                <input
                  type="checkbox"
                  name={step.key}
                  value={option.value}
                  checked={selectedValues.includes(option.value)}
                  onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                />
                <div className="option-content">
                  <div className="option-header">
                    <span className="option-name">{option.label}</span>
                    {option.priceModifier > 0 && (
                      <span className="price-modifier">+${option.priceModifier.toFixed(2)}</span>
                    )}
                  </div>
                  {option.description && (
                    <p className="option-description">{option.description}</p>
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Fallback: Single-select (radio) rendering
  return (
    <div className="option-selector">
      <p className="step-description">{step.description}</p>
      <div className="options-list">
        {step.options.map(option => (
          <div
            key={option.value}
            className={`option-item ${selectedValue === option.value ? 'selected' : ''}`}
          >
            <label className="option-label">
              <input
                type="radio"
                name={step.key}
                value={option.value}
                checked={selectedValue === option.value}
                onChange={() => handleRadioChange(option.value)}
              />
              <div className="option-content">
                <div className="option-header">
                  <span className="option-name">{option.label}</span>
                  {option.price !== undefined && option.price > 0 ? (
                    <span className="price-absolute">${option.price.toFixed(2)}</span>
                  ) : option.priceModifier > 0 ? (
                    <span className="price-modifier">+${option.priceModifier.toFixed(2)}</span>
                  ) : option.priceModifier === 0 ? (
                    <span className="price-included">Included</span>
                  ) : null}
                </div>
                {option.description && (
                  <p className="option-description">{option.description}</p>
                )}
                {option.sku && (
                  <p className="option-specs">SKU: {option.sku}</p>
                )}
                {option.specifications && (
                  <div className="option-specifications">
                    {option.specifications.weightCapacity && (
                      <p className="spec-item">Weight Capacity: {option.specifications.weightCapacity}</p>
                    )}
                    {option.specifications.heightRange && (
                      <p className="spec-item">Height Range: {option.specifications.heightRange}</p>
                    )}
                  </div>
                )}
                {option.dimensions && (
                  <p className="option-specs">Dimensions: {option.dimensions}"</p>
                )}
                {option.colorCode && (
                  <div className="color-preview" style={{ backgroundColor: option.colorCode }}></div>
                )}
              </div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OptionSelector;
